import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Rocket,
  Wallet,
  Network,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Info,
  Code,
  Link2
} from 'lucide-react';

const BlockchainDeployment = ({ contractId, smartContractData }) => {
  const { api } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState('sepolia');
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [instructions, setInstructions] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchNetworks();
    fetchDeployments();
    checkWalletConnection();
  }, [contractId]);

  const fetchNetworks = async () => {
    try {
      const response = await api.get('/blockchain/networks');
      setNetworks(response.data.networks || []);
    } catch (error) {
      console.error('Error fetching networks:', error);
    }
  };

  const fetchDeployments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${contractId}/deployments`);
      setDeployments(response.data.deployments || []);
    } catch (error) {
      console.error('Error fetching deployments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstructions = async () => {
    try {
      const response = await api.get(
        `/contracts/${contractId}/deploy/instructions?network=${selectedNetwork}`
      );
      setInstructions(response.data);
    } catch (error) {
      console.error('Error fetching instructions:', error);
      toast.error('Error al obtener instrucciones');
    }
  };

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setWalletConnected(true);
          setWalletAddress(accounts[0]);
        }
      } catch (error) {
        console.error('Error checking wallet:', error);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      toast.error('MetaMask no está instalado. Por favor, instala MetaMask.');
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      setWalletConnected(true);
      setWalletAddress(accounts[0]);
      toast.success('Wallet conectada');
      
      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        } else {
          setWalletConnected(false);
          setWalletAddress('');
        }
      });
      
    } catch (error) {
      console.error('Error connecting wallet:', error);
      toast.error('Error al conectar wallet');
    }
  };

  const switchNetwork = async (networkId) => {
    const network = networks.find(n => n.id === networkId);
    if (!network) return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${network.chain_id.toString(16)}` }],
      });
      setSelectedNetwork(networkId);
    } catch (switchError) {
      // Network not added, try to add it
      if (switchError.code === 4902) {
        try {
          const config = instructions?.rpc_config;
          if (config) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [config],
            });
            setSelectedNetwork(networkId);
          }
        } catch (addError) {
          toast.error('Error al agregar la red');
        }
      } else {
        toast.error('Error al cambiar de red');
      }
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copiado al portapapeles');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const recordDeployment = async (contractAddress, txHash) => {
    try {
      const response = await api.post(`/contracts/${contractId}/deploy`, {
        network: selectedNetwork,
        contract_address: contractAddress,
        transaction_hash: txHash,
        deployer_address: walletAddress,
        solidity_code: instructions?.smart_contract?.solidity_code || '',
        abi: [],
        constructor_args: []
      });
      
      toast.success('Despliegue registrado');
      fetchDeployments();
      return response.data;
    } catch (error) {
      console.error('Error recording deployment:', error);
      toast.error('Error al registrar despliegue');
    }
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const selectedNetworkInfo = networks.find(n => n.id === selectedNetwork);

  return (
    <div className="space-y-6" data-testid="blockchain-deployment">
      {/* Wallet Connection */}
      <div className="bg-slate-800/50 border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-blue-400" />
            Wallet
          </h3>
          {walletConnected ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-green-400 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Conectada
              </span>
              <span className="text-sm text-slate-400 font-mono">
                {truncateAddress(walletAddress)}
              </span>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
            >
              <Wallet className="w-4 h-4" />
              Conectar MetaMask
            </button>
          )}
        </div>
        
        {!walletConnected && (
          <div className="flex items-start gap-2 text-sm text-slate-400 bg-slate-900/50 rounded-lg p-3">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Necesitas conectar una wallet (MetaMask) para desplegar contratos en blockchain.
              Los tokens de prueba son gratuitos en las redes de testnet.
            </p>
          </div>
        )}
      </div>

      {/* Network Selection */}
      <div className="bg-slate-800/50 border border-border rounded-xl p-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Network className="w-5 h-5 text-purple-400" />
          Red de Despliegue
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {networks.map((network) => (
            <button
              key={network.id}
              onClick={() => {
                setSelectedNetwork(network.id);
                if (walletConnected) switchNetwork(network.id);
              }}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedNetwork === network.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-border hover:border-slate-600'
              }`}
            >
              <div className="font-medium">{network.name}</div>
              <div className="text-sm text-slate-400">
                Chain ID: {network.chain_id}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Moneda: {network.currency}
              </div>
              {network.faucet && (
                <a
                  href={network.faucet}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline flex items-center gap-1 mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  Obtener tokens gratis <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Deployment Instructions */}
      <div className="bg-slate-800/50 border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Rocket className="w-5 h-5 text-amber-400" />
            Despliegue
          </h3>
          <button
            onClick={fetchInstructions}
            disabled={!smartContractData}
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            Generar Instrucciones
          </button>
        </div>

        {instructions ? (
          <div className="space-y-4">
            <div className="bg-slate-900/50 rounded-lg p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Code className="w-4 h-4" />
                Código Solidity
              </h4>
              <div className="relative">
                <pre className="text-xs text-slate-300 overflow-x-auto max-h-60 overflow-y-auto p-3 bg-slate-950 rounded">
                  {instructions.smart_contract?.solidity_code || 'No disponible'}
                </pre>
                <button
                  onClick={() => copyToClipboard(
                    instructions.smart_contract?.solidity_code || '',
                    'solidity'
                  )}
                  className="absolute top-2 right-2 p-1.5 bg-slate-700 rounded hover:bg-slate-600 transition-colors"
                >
                  {copiedField === 'solidity' ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Pasos para desplegar:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-400">
                {instructions.steps?.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Después de desplegar, registra la transacción aquí para mantener un registro
                del contrato en blockchain vinculado a tu documento legal.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Code className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Genera las instrucciones de despliegue</p>
            <p className="text-sm mt-1">
              Se creará el código Solidity y los pasos necesarios
            </p>
          </div>
        )}
      </div>

      {/* Record Manual Deployment */}
      {walletConnected && (
        <ManualDeploymentRecorder 
          onRecord={recordDeployment}
          network={selectedNetwork}
        />
      )}

      {/* Previous Deployments */}
      {deployments.length > 0 && (
        <div className="bg-slate-800/50 border border-border rounded-xl p-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Link2 className="w-5 h-5 text-green-400" />
            Despliegues Anteriores
          </h3>
          
          <div className="space-y-3">
            {deployments.map((deployment) => (
              <div 
                key={deployment.id}
                className="bg-slate-900/50 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{deployment.network_name}</span>
                  <span className="text-xs text-slate-400">
                    {new Date(deployment.deployed_at).toLocaleDateString('es-CL')}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Contrato:</span>
                    <code className="text-xs text-blue-400">
                      {truncateAddress(deployment.contract_address)}
                    </code>
                    <a
                      href={deployment.explorer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">TX:</span>
                    <code className="text-xs text-slate-300">
                      {truncateAddress(deployment.transaction_hash)}
                    </code>
                    <a
                      href={deployment.tx_explorer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component for manual deployment recording
const ManualDeploymentRecorder = ({ onRecord, network }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contractAddress, setContractAddress] = useState('');
  const [txHash, setTxHash] = useState('');
  const [recording, setRecording] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contractAddress || !txHash) {
      toast.error('Completa todos los campos');
      return;
    }

    // Basic validation
    if (!contractAddress.startsWith('0x') || contractAddress.length !== 42) {
      toast.error('Dirección de contrato inválida');
      return;
    }
    if (!txHash.startsWith('0x') || txHash.length !== 66) {
      toast.error('Hash de transacción inválido');
      return;
    }

    setRecording(true);
    try {
      await onRecord(contractAddress, txHash);
      setContractAddress('');
      setTxHash('');
      setIsOpen(false);
    } finally {
      setRecording(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full py-3 border border-dashed border-slate-600 rounded-xl text-slate-400 hover:text-white hover:border-slate-400 transition-colors"
      >
        + Registrar Despliegue Manual
      </button>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-border rounded-xl p-4">
      <h4 className="font-medium mb-4">Registrar Despliegue en {network}</h4>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">
            Dirección del Contrato
          </label>
          <input
            type="text"
            value={contractAddress}
            onChange={(e) => setContractAddress(e.target.value)}
            placeholder="0x..."
            className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">
            Hash de Transacción
          </label>
          <input
            type="text"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            placeholder="0x..."
            className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm font-mono"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex-1 py-2 border border-slate-600 rounded-lg text-slate-400 hover:text-white hover:border-slate-400 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={recording}
            className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {recording ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4" />
                Registrar
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlockchainDeployment;
